/**
 * "Nice" values for grid spacing, in ascending order.
 * The algorithm picks the value from the appropriate decade closest to the
 * raw computed spacing so that labels/values remain human-readable.
 */
const NICE_STEPS = [1, 2, 5, 10];

/**
 * Snap a raw positive value to the nearest "nice" human-readable value.
 *
 * Works by finding the order of magnitude (power of 10), then picking the
 * closest step from `[1, 2, 5, 10]` within that decade.
 *
 * Uses a logarithmic distance metric so that snapping is proportional
 * (e.g., 37 is "closer" to 50 than to 20 on a log scale), which slightly
 * favors smaller/denser values — desirable for grid spacing.
 *
 * @param raw - A positive number to snap. Must be greater than 0.
 * @returns The nearest "nice" value (1, 2, 5, or 10 × 10^n)
 *
 * @example
 * ```ts
 * snapToNiceValue(37)    // → 50
 * snapToNiceValue(8)     // → 10
 * snapToNiceValue(130)   // → 100
 * snapToNiceValue(0.03)  // → 0.02
 * ```
 */
const snapToNiceValue = (raw: number): number => {
  if (raw <= 0 || !Number.isFinite(raw)) {
    throw new RangeError(`snapToNiceValue expects a positive finite number, got ${raw}`);
  }

  const magnitude = 10 ** Math.floor(Math.log10(raw));

  let best = NICE_STEPS[0] * magnitude;
  let bestDist = Number.POSITIVE_INFINITY;

  for (const step of NICE_STEPS) {
    const candidate = step * magnitude;
    const dist = Math.abs(Math.log(raw / candidate));
    if (dist < bestDist) {
      bestDist = dist;
      best = candidate;
    }
  }

  return best;
};

export { snapToNiceValue };
